import { watch } from "@arcgis/core/core/reactiveUtils";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import { webMercatorToGeographic } from "@arcgis/core/geometry/support/webMercatorUtils";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import { WORLD_MAP_CLUSTER_HIGHLIGHT_COLOR } from "@components/world-map/clustering/world-map-cluster-constants";
import { createClusterIndex, getClusterEntities, getClusterResult } from "@components/world-map/clustering/world-map-cluster-index";
import { createClusterSymbol, createMarkerSymbols } from "@components/world-map/clustering/world-map-cluster-symbols";
import { getClusterViewport, getClusterViewportKey } from "@components/world-map/clustering/world-map-cluster-viewport";
import { useCallback, useEffect, useMemo, useRef } from "react";

/** Latitudes outside this range mean the extent is not in geographic coordinates. */
const MAX_PLAUSIBLE_LATITUDE = 90;

/** Reads the viewport extent in geographic coordinates, or null when unavailable. */
function getGeographicExtent(view) {
  const extent = view.extent;
  if (!extent) {
    return null;
  }

  // A global SceneView can use either Web Mercator or WGS84 depending on its
  // basemap, and `webMercatorToGeographic` would collapse already-geographic
  // coordinates to a sliver around null island.
  const geographic = extent.spatialReference?.isWebMercator ? webMercatorToGeographic(extent) : extent;
  if (!geographic) {
    return null;
  }
  const {
    xmin,
    ymin,
    xmax,
    ymax
  } = geographic;

  // Fall back to the whole world rather than trusting an implausible extent.
  if (Math.abs(ymin) > MAX_PLAUSIBLE_LATITUDE || Math.abs(ymax) > MAX_PLAUSIBLE_LATITUDE) {
    return null;
  }
  return {
    xmin,
    ymin,
    xmax,
    ymax
  };
}

/** Translates the current camera into a supercluster query. */
function getViewportForView(view) {
  return getClusterViewport(view.zoom, getGeographicExtent(view), view.camera?.tilt ?? 0);
}
function getGeographicPoint(lng, lat) {
  return new Point({
    longitude: lng,
    latitude: lat,
    spatialReference: {
      wkid: 4326
    }
  });
}
function getMarkerGraphic(entity, symbol, isVisible) {
  return new Graphic({
    geometry: getGeographicPoint(entity.position.lng, entity.position.lat),
    symbol,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Name provided by ArcGIS API
    visible: isVisible,
    attributes: {
      id: entity.id,
      name: entity.name
    }
  });
}
function getClusterGraphic(cluster) {
  return new Graphic({
    geometry: getGeographicPoint(cluster.lng, cluster.lat),
    symbol: createClusterSymbol(cluster.label, cluster.count),
    attributes: {
      // Name kept from the 2D cluster contract that `isClusterGraphic` still reads.
      /* eslint-disable-next-line @typescript-eslint/naming-convention -- Name provided by ArcGIS API */
      cluster_count: cluster.count,
      clusterId: cluster.id,
      label: cluster.label
    }
  });
}

/** A graphic on the map, with the version of the data it was drawn from. */

/** A graphic the next frame should contain, created only if it is not already drawn. */

/** Changes whenever a pin's position or name does, so an edited project is redrawn. */
function getMarkerVersion(entity) {
  return `${entity.position.lng},${entity.position.lat},${entity.name}`;
}

/** Appends a graphic to its layer's list, creating the list on first use. */
function addToLayerGroup(groups, layer, graphic) {
  const group = groups.get(layer);
  if (group) {
    group.push(graphic);
  } else {
    groups.set(layer, [graphic]);
  }
}

/**
 * Adds and removes only what changed. Clearing and refilling the layers instead
 * makes every settle flash the whole marker set.
 */
function syncGraphics(rendered, specs) {
  const nextVersions = new Map(specs.map(spec => [spec.key, spec.version]));
  const removedByLayer = new Map();
  const addedByLayer = new Map();
  for (const [key, {
    graphic,
    version
  }] of rendered) {
    const layer = graphic.layer;
    const isCurrent = nextVersions.has(key) && nextVersions.get(key) === version;
    if (isCurrent || !(layer instanceof GraphicsLayer)) {
      continue;
    }
    addToLayerGroup(removedByLayer, layer, graphic);
    rendered.delete(key);
  }
  for (const spec of specs) {
    if (rendered.has(spec.key)) {
      continue;
    }
    const graphic = spec.create();
    rendered.set(spec.key, {
      graphic,
      version: spec.version
    });
    addToLayerGroup(addedByLayer, spec.layer, graphic);
  }
  removedByLayer.forEach((graphics, layer) => layer.removeMany(graphics));
  addedByLayer.forEach((graphics, layer) => layer.addMany(graphics));
}

/**
 * Reconciles both layers against the clustering result for the current camera.
 * Cluster keys include the zoom because supercluster only guarantees cluster ids
 * are stable within a single zoom level. A bubble's version is the index it came
 * from: when the entities change, an id can stand for a different group of
 * projects, so every bubble is rebuilt.
 */
function applyClusterGraphics({
  markerLayer,
  clusterLayer,
  rendered,
  index,
  result,
  viewport,
  markerSymbol,
  hiddenMarkerId
}) {
  const markerSpecs = result.markers.map(entity => ({
    key: `marker:${entity.id}`,
    version: getMarkerVersion(entity),
    layer: markerLayer,
    create: () => getMarkerGraphic(entity, markerSymbol, entity.id !== hiddenMarkerId)
  }));
  const clusterSpecs = result.clusters.map(cluster => ({
    key: `cluster:${viewport.zoom}:${cluster.id}`,
    version: index,
    layer: clusterLayer,
    create: () => getClusterGraphic(cluster)
  }));
  syncGraphics(rendered, [...markerSpecs, ...clusterSpecs]);
}

/**
 * Renders map entities as clusters on the 3D scene.
 *
 * ArcGIS has no cluster feature reduction in a `SceneView`, so the grouping is
 * computed client-side with supercluster and drawn as graphics. The results go to
 * two layers because `elevationInfo` is a per-layer setting and the two kinds of
 * graphic need opposite treatment: individual pins keep `relative-to-scene` so
 * they still sit on rooftops and lift when the camera tilts, while cluster
 * bubbles — which mark a centroid, not a place — use the cheaper billboarded
 * `relative-to-ground` and skip scene-depth sampling entirely.
 *
 * @param viewRef - Ref to the ArcGIS view. Clustering only applies to the 3D scene.
 * @param isViewReady - Whether the view has finished initializing.
 * @param entities - Entities to cluster. Rebuilding the index on change is O(n log n).
 * @param isMarkerLifted - Whether the camera is tilted past the marker lift threshold.
 */
export function useMapClustering(viewRef, isViewReady, entities, isMarkerLifted) {
  const clusterLayerRef = useRef(null);
  const markerLayerRef = useRef(null);

  // Graphics currently on the map, keyed so each settle can add and remove only
  // the difference instead of clearing both layers and flashing every marker.
  const renderedGraphicsRef = useRef(new Map());

  // Viewport the layers currently show, so a settle that did not move the camera
  // does no work at all.
  const renderedViewportKeyRef = useRef(null);
  const index = useMemo(() => createClusterIndex(entities), [entities]);
  const markerSymbols = useMemo(() => createMarkerSymbols(), []);

  // Read inside the ArcGIS watcher callback without re-registering it on flip.
  const isMarkerLiftedRef = useRef(isMarkerLifted);
  isMarkerLiftedRef.current = isMarkerLifted;

  // Highlight state lives here rather than in the highlight hook because a settle
  // can replace graphics: a pin re-created while hidden must be created hidden.
  const hiddenMarkerIdRef = useRef(null);
  const highlightedClusterRef = useRef(null);

  // Notified after each redraw. A separate `stationary` watcher in another hook
  // can't be relied on for this: it may run before this hook's watcher (which is
  // re-registered whenever the entities change) and see the old graphics.
  const renderListenersRef = useRef(new Set());
  const subscribeToRender = useCallback(listener => {
    const listeners = renderListenersRef.current;
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  const getClusterEntitiesForId = useCallback(clusterId => getClusterEntities(index, clusterId), [index]);
  const entitiesById = useMemo(() => new Map(entities.map(entity => [entity.id, entity])), [entities]);
  const getEntityPoint = useCallback(entityId => {
    const entity_0 = entitiesById.get(entityId);
    return entity_0 ? getGeographicPoint(entity_0.position.lng, entity_0.position.lat) : null;
  }, [entitiesById]);
  const findClusterGraphic = useCallback(entityId_0 => clusterLayerRef.current?.graphics.find(graphic => Boolean(getClusterEntities(index, Number(graphic.attributes?.clusterId))?.some(entity_1 => entity_1.id === entityId_0))) ?? null, [index]);
  const setHighlightedCluster = useCallback(clusterGraphic => {
    const previous = highlightedClusterRef.current;
    if (previous === clusterGraphic) {
      return;
    }
    if (previous) {
      previous.symbol = createClusterSymbol(String(previous.attributes?.label), Number(previous.attributes?.cluster_count));
    }
    if (clusterGraphic) {
      clusterGraphic.symbol = createClusterSymbol(String(clusterGraphic.attributes?.label), Number(clusterGraphic.attributes?.cluster_count), WORLD_MAP_CLUSTER_HIGHLIGHT_COLOR);
    }
    highlightedClusterRef.current = clusterGraphic;
  }, []);
  const setHiddenMarker = useCallback(entityId_1 => {
    const rendered = renderedGraphicsRef.current;
    const previousId = hiddenMarkerIdRef.current;
    if (previousId === entityId_1) {
      return;
    }
    const previous_0 = previousId ? rendered.get(`marker:${previousId}`)?.graphic : null;
    if (previous_0) {
      previous_0.visible = true;
    }
    const next = entityId_1 ? rendered.get(`marker:${entityId_1}`)?.graphic : null;
    if (next) {
      next.visible = false;
    }
    hiddenMarkerIdRef.current = entityId_1;
  }, []);

  // Create the two layers once the 3D view exists, and tear them down with it.
  useEffect(() => {
    const view = viewRef.current;
    const map = view?.map;
    if (!isViewReady || view?.type !== "3d" || !map) {
      return;
    }
    const markerLayer = new GraphicsLayer({
      elevationInfo: {
        mode: "relative-to-scene"
      }
    });
    const clusterLayer = new GraphicsLayer({
      elevationInfo: {
        mode: "relative-to-ground"
      }
    });
    markerLayerRef.current = markerLayer;
    clusterLayerRef.current = clusterLayer;
    map.addMany([markerLayer, clusterLayer]);
    const renderedGraphics = renderedGraphicsRef.current;
    return () => {
      map.removeMany([markerLayer, clusterLayer]);
      markerLayer.destroy();
      clusterLayer.destroy();
      markerLayerRef.current = null;
      clusterLayerRef.current = null;
      renderedGraphics.clear();
      renderedViewportKeyRef.current = null;
    };
  }, [viewRef, isViewReady]);

  // Rebuild the graphics whenever the camera settles, the container resizes, or
  // the entities change.
  useEffect(() => {
    const view_0 = viewRef.current;
    const markerLayer_0 = markerLayerRef.current;
    const clusterLayer_0 = clusterLayerRef.current;
    if (view_0?.type !== "3d" || !markerLayer_0 || !clusterLayer_0) {
      return;
    }

    // Re-bound after the guard so the nested render sees the narrowed types.
    const scene = view_0;
    const layers = {
      markerLayer: markerLayer_0,
      clusterLayer: clusterLayer_0
    };
    const rendered_0 = renderedGraphicsRef.current;
    function render(shouldForce) {
      const viewport = getViewportForView(scene);
      const viewportKey = getClusterViewportKey(viewport);
      if (!shouldForce && viewportKey === renderedViewportKeyRef.current) {
        return;
      }
      renderedViewportKeyRef.current = viewportKey;
      applyClusterGraphics({
        ...layers,
        rendered: rendered_0,
        index,
        viewport,
        result: getClusterResult(index, viewport.bbox, viewport.zoom),
        markerSymbol: isMarkerLiftedRef.current ? markerSymbols.lifted : markerSymbols.grounded,
        hiddenMarkerId: hiddenMarkerIdRef.current
      });
      renderListenersRef.current.forEach(listener_0 => listener_0());
    }
    render(true);
    const handle = watch(() => [scene.stationary, scene.width, scene.height], () => {
      if (scene.stationary) {
        render(false);
      }
    });
    return () => handle.remove();
  }, [viewRef, isViewReady, index, markerSymbols]);

  // Reassign the shared symbol when the tilt threshold flips. A GraphicsLayer has
  // no renderer to swap, so each pin is pointed at the other shared instance.
  useEffect(() => {
    const markerLayer_1 = markerLayerRef.current;
    if (!markerLayer_1) {
      return;
    }
    const symbol = isMarkerLifted ? markerSymbols.lifted : markerSymbols.grounded;
    markerLayer_1.graphics.forEach(graphic_0 => {
      graphic_0.symbol = symbol;
    });
  }, [isMarkerLifted, isViewReady, markerSymbols]);
  return {
    markerLayerRef,
    clusterLayerRef,
    getClusterEntities: getClusterEntitiesForId,
    getEntityPoint,
    findClusterGraphic,
    setHighlightedCluster,
    setHiddenMarker,
    subscribeToRender
  };
}